import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';

import { fetchQueuesWithPromise } from 'flow/actions/pbx-actions';
import { fetchWallboards } from 'flow/actions/wallboard-actions';
import DashboardIntro from 'flow/components/dashboard/DashboardIntro';
import DashboardsOverview from 'flow/components/dashboard/DashboardsOverview';
import Loading from 'flow/components/ui-components/Loading';
import useAppContext from 'flow/hooks/useAppContext';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';

import { generatePath } from 'flow/utils/router-utils';
import Page from './Page';

const Dashboards = () => {
    const { i18next } = useAppContext();
    const dispatch: any = useAppDispatch();
    const history = useHistory();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const wallboards = useAppSelector((state) =>
        state.wallboards.get('wallboards'),
    );
    const queues = useAppSelector((state) => state.pbx.get('queues'));

    const [isFetchingQueues, setIsFetchingQueues] = useState(true);
    const [isFetchingWallboards, setIsFetchingWallboards] = useState(true);

    const { isSinglePaneMode } = useSinglePaneMode();

    useEffect(() => {
        setIsFetchingQueues(true);
        setIsFetchingWallboards(true);
        dispatch(fetchQueuesWithPromise()).finally(() => {
            setIsFetchingQueues(false);
        });

        dispatch(fetchWallboards()).finally(() => {
            setIsFetchingWallboards(false);
        });
    }, [setIsFetchingQueues, setIsFetchingWallboards]);

    const displayLoading = useMemo(() => {
        return (
            (isFetchingQueues && (!queues || queues.isEmpty())) ||
            (isFetchingWallboards && (!wallboards || wallboards.isEmpty()))
        );
    }, [isFetchingQueues, isFetchingWallboards, queues, wallboards]);

    const Content = () => {
        if (displayLoading) return <Loading />;

        if (!queues || queues.isEmpty()) {
            return (
                <div className="mt-9 self-center text-lg text-gray-500">
                    {i18next.t('wallboard.create.queue_required')}
                </div>
            );
        }

        if (!wallboards || wallboards.isEmpty()) {
            return <DashboardIntro />;
        }

        return <DashboardsOverview />;
    };

    return (
        <Page
            header="settings.dashboards"
            onClickBack={
                isSinglePaneMode
                    ? () =>
                          history.push(
                              generatePath(routes['settings'], {
                                  extensionKey,
                              }),
                          )
                    : undefined
            }
        >
            <Content />
        </Page>
    );
};

export default Dashboards;
